import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../utils/api';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'customer' | 'admin';
  avatar?: string;
}

interface ValidationError {
  code: string;
  description: string;
}

interface RegisterResult {
  success: boolean;
  errors?: ValidationError[];
  message?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (userData: RegisterData) => Promise<RegisterResult>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  cartCount: number;
  setCartCount: (n: number) => void;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const init = async () => {
        try {
          // Fast path: use a readable indicator (cookie OR localStorage) to decide if we should call auth endpoints
          const hasReadableLoginIndicator = (() => {
            try {
              // Cookie check
              if (typeof document !== 'undefined') {
                const cookies = document.cookie ? document.cookie.split('; ') : [];
                for (const c of cookies) {
                  if (c.startsWith('logged_in=')) {
                    const v = c.substring('logged_in='.length);
                    if (v === '1') return true;
                  }
                }
              }
              // LocalStorage fallback (non-sensitive flag only)
              if (typeof window !== 'undefined' && window.localStorage) {
                if (window.localStorage.getItem('rf_logged_in') === '1') return true;
              }
            } catch {}
            return false;
          })();

          if (!hasReadableLoginIndicator) {
            // Not logged in → avoid calling /auth/* endpoints
            setUser(null);
            setIsLoading(false);
            return;
          }

          // Check if we're on a payment-related page (success/failed)
          const isPaymentPage = window.location.pathname.includes('/payment/');
          
          
          // For payment pages, prioritize immediate authentication check with retry
        if (isPaymentPage) {
          const maxRetries = 2;
          let retryCount = 0;
          
          const attemptAuth = async (): Promise<void> => {
            try {
              const { data } = await api.get('/auth/me');
              if (data) {
                const userData = { id: data.id || '', email: data.email, firstName: data.firstName || '', lastName: data.lastName || '', role: (data.role || data.roles?.[0] || 'customer') };
                setUser(userData);
                try { if (typeof window !== 'undefined') window.localStorage?.setItem('rf_logged_in', '1'); } catch {}
              }
            } catch (err: any) {
              // For payment pages, try refresh if we get 401 and haven't exceeded retries
              if (err?.response?.status === 401 && retryCount < maxRetries) {
                retryCount++;
                try {
                  await api.post('/auth/refresh');
                  await attemptAuth(); // Retry the auth check
                } catch {
                  // Refresh failed, user will need to login
                  setUser(null);
                }
              } else {
                setUser(null);
              }
            }
          };
          
          await attemptAuth();
          setIsLoading(false);
          return;
        }
        
        // For non-payment pages, normal authentication flow
        const attemptAuth = async (): Promise<void> => {
          try {
            const { data } = await api.get('/auth/me');
            if (data) {
              const userData = { id: data.id || '', email: data.email, firstName: data.firstName || '', lastName: data.lastName || '', role: (data.role || data.roles?.[0] || 'customer') };
              setUser(userData);
              try { if (typeof window !== 'undefined') window.localStorage?.setItem('rf_logged_in', '1'); } catch {}
            }
          } catch (err: any) {
            if (err?.response?.status === 401) {
              // Attempt a single silent refresh then retry
              try {
                await api.post('/auth/refresh');
                await attemptAuth(); // Retry the auth check
              } catch {
                // Refresh failed, user will need to login
                setUser(null);
                try { if (typeof window !== 'undefined') window.localStorage?.removeItem('rf_logged_in'); } catch {}
              }
            } else {
              // Other errors, user will need to login
              setUser(null);
              try { if (typeof window !== 'undefined') window.localStorage?.removeItem('rf_logged_in'); } catch {}
            }
          }
        };
        
        await attemptAuth();
      } catch {}
      finally { setIsLoading(false); }
    };
    init();
  }, []);

  // Initialize cart count when authenticated
  useEffect(() => {
    const initCart = async () => {
      if (!user) { setCartCount(0); return; }
      try {
        const { data } = await api.get('/cart');
        const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
        const total = items.reduce((sum: number, it: any) => sum + Number(it.quantity || it.Quantity || 0), 0);
        setCartCount(total);
      } catch {
        // ignore
      }
    };
    initCart();
  }, [user]);

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      await api.post('/auth/login', { email, password });
      // fetch profile after cookies set
      const me = await api.get('/auth/me');
      const prof = me.data || {};
      const logged: User = { id: prof.id || '', email: prof.email || email, firstName: prof.firstName || '', lastName: prof.lastName || '', role: (prof.role as 'customer' | 'admin') || (prof.roles?.[0] as any) || 'customer' };
      setUser(logged);
      try { if (typeof window !== 'undefined') window.localStorage?.setItem('rf_logged_in', '1'); } catch {}
      setIsLoading(false);
      return true;
    } catch (error: any) {
      setIsLoading(false);
      // Re-throw the error so the Login component can handle it with proper localization
      throw error;
    }
  };

  const register = async (userData: RegisterData): Promise<RegisterResult> => {
    setIsLoading(true);
    try {
      await api.post('/auth/register', userData);
      setIsLoading(false);
      return { success: true };
    } catch (error: any) {
      setIsLoading(false);
      const responseData = error?.response?.data;
      const errors = responseData?.errors || [];
      const message = responseData?.message || 'Registration failed';
      
      return {
        success: false,
        errors: errors,
        message: message
      };
    }
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    setUser(null);
    setCartCount(0);
    try { if (typeof window !== 'undefined') window.localStorage?.removeItem('rf_logged_in'); } catch {}
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      try { if (typeof window !== 'undefined') window.localStorage?.setItem('rf_logged_in', '1'); } catch {}
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    updateUser,
    cartCount,
    setCartCount,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
